import axios from "axios";
import Cookies from "js-cookie";
import {
  ZephrUserProfile,
  ZephrUserProfileUpdate,
} from "lib/zephr/profile-types";
import { datadogLogger } from "lib/datadog/logger";

/**
 * Gets the current user's profile from Zephr
 * Uses the blaize_session cookie for authentication
 * @param cookie - The blaize_session cookie value (optional, will read from cookies if not provided)
 * @returns The user's profile data
 */
export async function getZephrUserProfile(
  cookie?: string
): Promise<ZephrUserProfile | null> {
  try {
    const sessionCookie = cookie || Cookies.get("blaize_session");

    if (!sessionCookie) {
      datadogLogger.warn("No blaize_session cookie found");
      return null;
    }

    const response = await axios.get<ZephrUserProfile>(
      "/blaize/profile",
      process.env.JISC_ENVIRONMENT === "development"
        ? { withCredentials: true }
        : {
            headers: {
              Cookie: `blaize_session=${sessionCookie}`,
            },
          }
    );

    return response.data;
  } catch (error) {
    datadogLogger.error("Error fetching Zephr user profile", undefined, error);
    throw error;
  }
}

/**
 * Updates the current user's profile in Zephr
 * Uses PATCH method to update only the provided fields
 * @param customFieldData - Partial user profile data to update
 * @param cookie - The blaize_session cookie value (optional, will read from cookies if not provided)
 * @returns The response from the Zephr API
 */
export async function updateZephrUserProfile(
  customFieldData: ZephrUserProfileUpdate,
  cookie?: string
): Promise<{ success: boolean; message?: string; errors?: string[] }> {
  try {
    const sessionCookie = cookie || Cookies.get("blaize_session");

    if (!sessionCookie) {
      datadogLogger.warn("No blaize_session cookie found for update");
      return {
        success: false,
        errors: ["No authentication cookie found"],
      };
    }

    const response = await axios.patch("/blaize/profile", customFieldData, {
      headers: {
        "Content-Type": "application/json",
        Cookie: `blaize_session=${sessionCookie}`,
      },
    });

    return {
      success: true,
      message: response.data?.message || "User profile updated",
    };
  } catch (error) {
    datadogLogger.error("Error updating Zephr user profile", undefined, error);

    if (axios.isAxiosError(error)) {
      return {
        success: false,
        errors: [
          error.response?.data?.message ||
            error.message ||
            "Failed to update profile",
        ],
      };
    }

    return {
      success: false,
      errors: ["An unexpected error occurred"],
    };
  }
}

/**
 * Legacy wrapper to maintain compatibility with Piano-based code
 * Now calls updateZephrUserProfile directly instead of API route
 * @deprecated Use updateZephrUserProfile directly
 */
export async function updateCustomFields(
  cookie: string,
  customFieldData: ZephrUserProfileUpdate,
  userId?: string,
  formId?: string
) {
  return updateZephrUserProfile(customFieldData, cookie);
}
